import {
  Avatar,
  Button,
  Card,
  Chip,
  Container,
  DialogTitle,
  Divider,
  Grid,
  ListItem,
  ListItemAvatar,
  makeStyles,
  Typography
} from "@material-ui/core";
import { useQuery } from "@redux-requests/react";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import { filter as _filter, find as _find, isEmpty as _isEmpty, take as _take } from "lodash";
import { useIntl } from "react-intl";
import { Formik } from "formik";
import { Delete, Edit, Reply, Share } from "@material-ui/icons";
import { DeleteDialog, Flaticon, Loader, NoQAData, PdfPreview } from "../../components";
import {
  createModuleQuestion,
  deleteQuestionAnswer,
  getModule,
  getModuleQAs,
  updateModule,
  updateQuestionAnswer
} from "../../ducks/actions";
import * as types from "../../ducks/types";
import { useActionRequest, useNotifications, useUserMe } from "../../hooks";
import VideoPlayer from "../../components/VideoPlayer";
import { SubmitButton, TextField } from "../../components/formik";
import EditQuestionAnswerDialog from "../eposters/EditQuestionAnswerDialog";

const useStyles = makeStyles(theme => ({
  moduleContainer: {
    paddingLeft: 0,
    marginLeft: 0
  },
  gridContainer: {
    paddingTop: "1rem",
    paddingBottom: "1rem"
  },
  title: {
    fontSize: "2rem",
    color: "#224175"
  },
  mediaCard: {
    justifyContent: "center",
    width: "100%"
  },
  mediaPlayer: {
    width: "100%",
    height: "50vh"
  },
  chip: {
    margin: "0.2rem"
  },
  formSubmitButton: {
    marginTop: "16px",
    marginBottom: "8px"
  },

  previewImage: {
    height: "100%",
    width: "100%"
  },
  gridItemQA: {
    marginBottom: "1rem"
  },
  qaActions: {
    "& > *": {
      marginRight: theme.spacing(1)
    }
  },
  questionFormItem: {
    [theme.breakpoints.down("md")]: {
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(1)
    }
  }
}));

function getFirstLetterDotted(firstName) {
  return `${firstName?.toUpperCase().substring(0, 1)}.`;
}

const ModulePage = () => {
  const [oldCompletionPercentage, setOldCompletionPercentage] = useState(0);
  const [disableSkipForward, setDisableSkipForward] = useState(false);
  const [showAllQA, setShowAllQA] = useState(false);
  const [selectedModuleQA, setSelectedModuleQA] = useState(null);
  const [deleteModuleQaModalData, setDeleteModuleQaModalData] = useState({
    isOpen: false,
    questionId: null
  });

  const dispatch = useDispatch();
  const history = useHistory();
  const classes = useStyles();
  const intl = useIntl();

  const { idModule } = useParams();
  const { ko } = useNotifications();
  const { user } = useUserMe();

  useEffect(() => {
    dispatch(getModule({ idModule }));
    dispatch(getModuleQAs({ idModule }));
  }, [dispatch, idModule]);

  const {
    data: { course_module: courseModule },
    loading: courseModuleLoading,
    error: courseModuleError
  } = useQuery({ type: types.GET_MODULE, defaultData: { course_module: {} } });

  if (courseModuleError) {
    ko("Non hai accesso al modulo selezionato. Riprova più tardi.");
    history.goBack();
  }

  const {
    data: { data: qas },
    loading: qasLoading
  } = useQuery({ type: types.GET_MODULE_QAS, defaultData: { data: [] } });

  useEffect(() => {
    setOldCompletionPercentage(courseModule.completion_percentage);
    setDisableSkipForward(courseModule.completion_percentage !== 100);
  }, [courseModule]);

  const publishedQas = _filter(qas, qa => qa.published);
  const unpublishedQas = _filter(qas, qa => !qa.published && !!qa.answer);
  const unansweredQas = _filter(qas, qa => !qa.answer);

  const updateModuleRequest = useActionRequest({
    action: updateModule,
    successMessage: false,
    errorMessage: false
  });

  const createQuestionAnswerRequest = useActionRequest({
    action: createModuleQuestion,
    successMessage:
      "Your question has been addressed to the author and it will be visible as soon as the author will reply"
  });

  const shareModuleQARequest = useActionRequest({
    action: updateQuestionAnswer,
    successMessage: "The answer has been updated"
  });

  const deleteQuestionAnswerRequest = useActionRequest({
    action: deleteQuestionAnswer,
    successMessage: "The answer has been deleted"
  });

  const onQuestionAnswerCreate = ({ question }, { resetForm }) => {
    createQuestionAnswerRequest({ idModule, question });
    resetForm();
  };

  const confirmDeleteQa = ({ id }) => () => {
    setDeleteModuleQaModalData({ isOpen: false, questionId: null });
    deleteQuestionAnswerRequest({ id });
  };

  const onQuestionAnswerShared = ({ questionAnswer }) => () => {
    shareModuleQARequest({ id: questionAnswer.id, questionAnswer });
  };

  const onEditModuleQADialogOpen = ({ questionAnswer }) => () => {
    setSelectedModuleQA(questionAnswer);
  };

  const onEditModuleQADialogClose = () => {
    setSelectedModuleQA(null);
  };

  const openDeleteQaModal = ({ id }) => () => {
    setDeleteModuleQaModalData({ isOpen: true, questionId: id });
  };

  const closeDeleteQaModal = () => {
    setDeleteModuleQaModalData({ isOpen: false, questionId: null });
  };

  const onProgress = ({ played }) => {
    const completionPercentage = Math.round(played * 100);
    if (completionPercentage - oldCompletionPercentage >= 10 || completionPercentage === 100) {
      updateModuleRequest({ idModule, completionPercentage });
      setOldCompletionPercentage(completionPercentage);
    }
  };

  const pdfPageChanged = ({ page, totalPages }) => {
    const completionPercentage = (page * 100) / totalPages;
    if (completionPercentage > oldCompletionPercentage || completionPercentage === 100) {
      updateModuleRequest({ idModule, completionPercentage });
      setOldCompletionPercentage(completionPercentage);
    }
  };

  const showAllQuestion = () => {
    setShowAllQA(true);
  };

  const goBackToCourse = () => {
    history.goBack();
  };

  return (
    <>
      <Container maxWidth="lg" className={classes.moduleContainer}>
        <Loader loading={courseModuleLoading || qasLoading}>
          <Grid container className={classes.gridContainer}>
            {courseModule.completion_percentage === 100 && (
              <Grid item xs={12}>
                <Button variant="text" size="large" color="primary" onClick={goBackToCourse}>
                  &lt;&nbsp;&nbsp;
                  {intl.formatMessage({
                    id: "Pages.CourseModule.GoBackToCourse",
                    defaultMessage: "Go back to course"
                  })}
                </Button>
              </Grid>
            )}
          </Grid>
          <Grid container className={(classes.gridContainer, classes.mediaCard)}>
            <Card elevation={0} className={classes.mediaCard}>
              {!_isEmpty(courseModule.recording) ? (
                <div className={classes.mediaPlayer}>
                  <VideoPlayer
                    videoUrl={courseModule.recording.video_url}
                    startingPercentage={courseModule.completion_percentage}
                    disableSkipForward={disableSkipForward}
                    onProgress={onProgress}
                  />
                </div>
              ) : (
                !_isEmpty(courseModule.document) && (
                  <div className={classes.mediaPlayer}>
                    <PdfPreview
                      documentUrl={courseModule.document.pdf.url}
                      pageChanged={pdfPageChanged}
                    />
                  </div>
                )
              )}
            </Card>
          </Grid>
          <Grid container className={classes.gridContainer}>
            <Grid item md={10} xs={12}>
              <Typography id="moduleTitle" variant="body1" className={classes.title}>
                <strong>{courseModule.title}</strong>
              </Typography>
            </Grid>
            {!_isEmpty(courseModule.document) && !_isEmpty(courseModule.document.pdf) && (
              <Grid item md={2} xs={12}>
                <Button
                  variant="contained"
                  size="large"
                  color="primary"
                  href={courseModule.document.pdf.url}
                  download
                  target="_blank"
                  disableElevation
                >
                  Download PDF
                </Button>
              </Grid>
            )}
          </Grid>
          <Divider />
          <Grid container id="teacherName" className={classes.gridContainer}>
            <Grid item md={1} xs={2}>
              <Flaticon icon="user" color="primary" margin={1} />
            </Grid>

            <Grid item container md={11} xs={10}>
              <Grid item xs={12}>
                <Typography variant="body1">
                  <strong>
                    {" "}
                    {intl.formatMessage({
                      id: "Pages.CourseModule.Teacher",
                      defaultMessage: "Teacher"
                    })}
                  </strong>
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body1">
                  {courseModule.teachers
                    ?.map(
                      ({ first_name: firstName, last_name: lastName }) =>
                        `${getFirstLetterDotted(firstName)} ${lastName}`
                    )
                    .join(", ")}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          <Divider />
          <Grid container id="desctiptionAndTags" className={classes.gridContainer}>
            <Grid item xs={12}>
              <Typography variant="body1">{courseModule?.description}</Typography>
            </Grid>
            <Grid item xs={12} className={classes.gridContainer}>
              {courseModule.tag_list?.map(tag => (
                <Chip
                  className={classes.chip}
                  variant="contained"
                  disableElevation
                  key={tag}
                  label={tag}
                />
              ))}
            </Grid>
          </Grid>
          <Divider />
          {_find(courseModule.teachers, x => x.id === user.profile?.id) ? (
            <>
              <Grid container id="questionAndAnswers" className={classes.gridContainer}>
                <Grid item xs={12}>
                  <Typography variant="h5">
                    {intl.formatMessage({
                      id: "Pages.CourseModule.QuestionsAndAnswers",
                      defaultMessage: "Questions and Answers"
                    })}
                  </Typography>
                </Grid>
                <NoQAData data={publishedQas}>
                  <Grid container className={classes.gridContainer}>
                    {(showAllQA ? publishedQas : _take(publishedQas, 2)).map(publishedQa => (
                      <Grid
                        item
                        container
                        key={publishedQa.id}
                        xs={12}
                        className={classes.gridItemQA}
                      >
                        <ListItem>
                          <ListItemAvatar>
                            <Avatar src={publishedQa.profile?.avatarThumb}>
                              <Flaticon icon="question" />
                            </Avatar>
                          </ListItemAvatar>
                          <Typography variant="body1">
                            {`${publishedQa.profile?.firstName} ${publishedQa.profile?.lastName}:`}{" "}
                            <strong>{publishedQa.question}</strong>
                          </Typography>
                        </ListItem>
                        <ListItem>
                          <ListItemAvatar>
                            <Avatar src={publishedQa.answerer?.avatarThumb} />
                          </ListItemAvatar>
                          <Typography variant="body1">
                            {publishedQa.answerer && (
                              <>{`${publishedQa.answerer.firstName} ${publishedQa.answerer.lastName}: `}</>
                            )}
                            <i>{publishedQa.answer}</i>
                          </Typography>
                        </ListItem>
                        {publishedQa.answerer?.id === user.profile?.id && (
                          <ListItem className={classes.qaActions}>
                            <Button
                              variant="contained"
                              disableElevation
                              color="primary"
                              onClick={onEditModuleQADialogOpen({
                                questionAnswer: publishedQa
                              })}
                            >
                              <Edit />
                              {intl.formatMessage({
                                id: "Pages.CourseModule.Edit",
                                defaultMessage: "Edit"
                              })}
                            </Button>
                            <Button
                              variant="contained"
                              disableElevation
                              color="primary"
                              onClick={openDeleteQaModal({ id: publishedQa.id })}
                            >
                              <Delete />
                              {intl.formatMessage({
                                id: "Pages.CourseModule.Delete",
                                defaultMessage: "Delete"
                              })}
                            </Button>
                          </ListItem>
                        )}
                      </Grid>
                    ))}
                    {!showAllQA && publishedQas.length > 2 && (
                      <Button variant="contained" color="primary" onClick={showAllQuestion}>
                        Show {publishedQas.length - 2} more question
                      </Button>
                    )}
                  </Grid>
                </NoQAData>
              </Grid>
              <Divider />
              <Grid container id="questionsWithoutAnswer" className={classes.gridContainer}>
                <Grid item xs={12}>
                  <Typography variant="h5">
                    {intl.formatMessage({
                      id: "Pages.CourseModule.QuestionsWithoutAnswer",
                      defaultMessage: "Questions Without Answers"
                    })}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body1">
                    {intl.formatMessage({
                      id: "Pages.CourseModule.QuestionsWithoutAnswerHelper",
                      defaultMessage:
                        "Questions and comments sent to you will be publicly visible only if and when you decide to answer them."
                    })}
                  </Typography>
                </Grid>

                <NoQAData data={unansweredQas}>
                  <Grid container className={classes.gridContainer}>
                    {unansweredQas.map(({ id, question, profile, answer, published }) => (
                      <Grid item container key={id} xs={12} className={classes.gridItemQA}>
                        <ListItem>
                          <ListItemAvatar>
                            <Avatar src={profile?.avatarThumb}>
                              <Flaticon icon="question" />
                            </Avatar>
                          </ListItemAvatar>
                          <Typography variant="body1">
                            {`${profile?.firstName} ${profile?.lastName}:`}{" "}
                            <strong>{question}</strong>
                          </Typography>
                        </ListItem>
                        <ListItem className={classes.qaActions}>
                          <Button
                            variant="contained"
                            disableElevation
                            color="primary"
                            onClick={onEditModuleQADialogOpen({
                              questionAnswer: { id, question, answer, published }
                            })}
                          >
                            <Reply />
                            {intl.formatMessage({
                              id: "Pages.CourseModule.Reply",
                              defaultMessage: "Reply"
                            })}
                          </Button>
                          <Button
                            variant="contained"
                            disableElevation
                            color="primary"
                            onClick={openDeleteQaModal({ id })}
                          >
                            <Delete />
                            {intl.formatMessage({
                              id: "Pages.CourseModule.Delete",
                              defaultMessage: "Delete"
                            })}
                          </Button>
                        </ListItem>
                      </Grid>
                    ))}
                  </Grid>
                </NoQAData>
              </Grid>
              <Divider />
              <Grid container id="unpublishedQuestionsAndAnswers" className={classes.gridContainer}>
                <Grid item xs={12}>
                  <Typography variant="h5">
                    {intl.formatMessage({
                      id: "Pages.CourseModule.UnpublishedQuestionsAndAnswers",
                      defaultMessage: "Unpublished Questions And Answers"
                    })}
                  </Typography>
                </Grid>

                <NoQAData data={unpublishedQas}>
                  <Grid container className={classes.gridContainer}>
                    {unpublishedQas.map(unpublishedQa => (
                      <Grid
                        item
                        container
                        key={unpublishedQa.id}
                        xs={12}
                        className={classes.gridItemQA}
                      >
                        <ListItem>
                          <ListItemAvatar>
                            <Avatar src={unpublishedQa.profile?.avatarThumb}>
                              <Flaticon icon="question" />
                            </Avatar>
                          </ListItemAvatar>
                          <Typography variant="body1">
                            {`${unpublishedQa.profile?.firstName} ${unpublishedQa.profile?.lastName}:`}{" "}
                            <strong>{unpublishedQa.question}</strong>
                          </Typography>
                        </ListItem>
                        <ListItem>
                          <ListItemAvatar>
                            <Avatar src={unpublishedQa.answerer?.avatarThumb} />
                          </ListItemAvatar>
                          <Typography variant="body1">
                            {unpublishedQa.answerer && (
                              <>{`${unpublishedQa.answerer.firstName} ${unpublishedQa.answerer.lastName}: `}</>
                            )}
                            <i>{unpublishedQa.answer}</i>
                          </Typography>
                        </ListItem>
                        {unpublishedQa.answerer?.id === user.profile?.id && (
                          <ListItem className={classes.qaActions}>
                            <Button
                              variant="contained"
                              disableElevation
                              color="primary"
                              onClick={onQuestionAnswerShared({
                                questionAnswer: {
                                  ...unpublishedQa,
                                  published: true
                                }
                              })}
                            >
                              <Share />
                              {intl.formatMessage({
                                id: "Pages.CourseModule.Share",
                                defaultMessage: "Publish"
                              })}
                            </Button>
                            <Button
                              variant="contained"
                              disableElevation
                              color="primary"
                              onClick={onEditModuleQADialogOpen({
                                questionAnswer: unpublishedQa
                              })}
                            >
                              <Edit />
                              {intl.formatMessage({
                                id: "Pages.CourseModule.Edit",
                                defaultMessage: "Edit"
                              })}
                            </Button>
                            <Button
                              variant="contained"
                              disableElevation
                              color="primary"
                              onClick={openDeleteQaModal({ id: unpublishedQa.id })}
                            >
                              <Delete />
                              {intl.formatMessage({
                                id: "Pages.CourseModule.Delete",
                                defaultMessage: "Delete"
                              })}
                            </Button>
                          </ListItem>
                        )}
                      </Grid>
                    ))}
                  </Grid>
                </NoQAData>
              </Grid>
            </>
          ) : (
            <Grid container id="questionAndAnswers" className={classes.gridContainer}>
              <Grid item xs={12}>
                <Typography variant="h5">
                  {intl.formatMessage({
                    id: "Pages.CourseModule.QuestionsAndAnswers",
                    defaultMessage: "Questions and Answers"
                  })}
                </Typography>
              </Grid>

              <Formik initialValues={{ question: "" }} onSubmit={onQuestionAnswerCreate}>
                <Grid container justify="space-between">
                  <Grid item xs={12} md={9} className={classes.questionFormItem}>
                    <TextField
                      name="question"
                      variant="outlined"
                      fullWidth
                      label={intl.formatMessage({
                        id: "Pages.CourseModule.AddQuestion",
                        defaultMessage: "Add a question"
                      })}
                    />
                  </Grid>
                  <Grid item xs={12} md={2} className={classes.questionFormItem}>
                    <SubmitButton
                      className={classes.formSubmitButton}
                      variant="contained"
                      color="primary"
                      fullWidth
                      defaultEnabled
                    >
                      {intl.formatMessage({
                        id: "Pages.CourseModule.SendQuestion",
                        defaultMessage: "Send Question"
                      })}
                    </SubmitButton>
                  </Grid>
                </Grid>
              </Formik>

              <NoQAData data={publishedQas}>
                <Grid container className={classes.gridContainer}>
                  {(showAllQA ? publishedQas : _take(publishedQas, 2)).map(
                    ({ id, question, answer, profile, answerer }) => (
                      <Grid item container key={id} xs={12} className={classes.gridItemQA}>
                        <ListItem>
                          <ListItemAvatar>
                            <Avatar src={profile?.avatarThumb}>
                              <Flaticon icon="question" />
                            </Avatar>
                          </ListItemAvatar>
                          <Typography variant="body1">
                            {`${profile?.firstName} ${profile?.lastName}:`}{" "}
                            <strong>{question}</strong>
                          </Typography>
                        </ListItem>
                        <ListItem>
                          <ListItemAvatar>
                            <Avatar src={answerer?.avatar_thumb} />
                          </ListItemAvatar>
                          <Typography variant="body1">
                            {answerer && <>{`${answerer.firstName} ${answerer.lastName}: `}</>}
                            <i>{answer}</i>
                          </Typography>
                        </ListItem>
                      </Grid>
                    )
                  )}
                  {!showAllQA && publishedQas.length > 2 && (
                    <Button variant="contained" color="primary" onClick={showAllQuestion}>
                      Show {publishedQas.length - 2} more question
                    </Button>
                  )}
                </Grid>
              </NoQAData>
            </Grid>
          )}
        </Loader>
        {selectedModuleQA && (
          <EditQuestionAnswerDialog
            questionAnswer={selectedModuleQA}
            onClose={onEditModuleQADialogClose}
          />
        )}

        {deleteModuleQaModalData.isOpen && (
          <DeleteDialog
            id={deleteModuleQaModalData.questionId}
            onDelete={confirmDeleteQa({ id: deleteModuleQaModalData.questionId })}
            onClose={closeDeleteQaModal}
          >
            <DialogTitle>
              {intl.formatMessage({
                id: "Pages.CourseModule.DeleteDialog.Title",
                defaultMessage: "Are you sure you want to cancel this question from the module?"
              })}
            </DialogTitle>
          </DeleteDialog>
        )}
      </Container>
    </>
  );
};

export default ModulePage;
